<template>
  <section
    id="checkoutAddress"
    :class="[openClass, 'expandable_section']"
  >
    <h2
      class="expandable_header"
      @click="updateSection(sectionName)"
    >
      {{ number }}. {{ $t('checkout.address.billingAndShipping') }}
    </h2>

    <div class="expandable_content">
      <div
        v-if="selected"
        class="checkout-address"
      >
        <p class="address-disclaimer">
          {{ $t('checkout.address.disclaimer') }}
        </p>

        <div class="required-field">
          <input
            id="checkout_first_name"
            v-model="firstName"
            v-validate="'required'"
            name="first_name"
            type="text"
            :placeholder="$t('checkout.address.firstName')"
            :class="{ 'required': errors.has('first_name') }"
            @change="$emit('payment-fields-update', { firstName: firstName })"
          >
          <span
            v-show="errors.has('first_name')"
            class="error"
          >
            {{ errors.first('first_name') }}
          </span>
        </div>

        <div class="required-field">
          <input
            id="checkout_last_name"
            v-model="lastName"
            v-validate="'required'"
            name="last_name"
            type="text"
            :placeholder="$t('checkout.address.lastName')"
            :class="{ 'required': errors.has('last_name') }"
            @change="$emit('payment-fields-update', { lastName: lastName })"
          >
          <span
            v-show="errors.has('last_name')"
            class="error"
          >
            {{ errors.first('last_name') }}
          </span>
        </div>

        <div class="required-field">
          <input
            id="checkout_phone_number"
            v-model="phoneNumber"
            v-validate="'required'"
            name="phone_number"
            type="text"
            :placeholder="$t('checkout.address.phoneNumber')"
            :class="{ 'required': errors.has('phone_number') }"
            @change="$emit('payment-fields-update', { phoneNumber: phoneNumber })"
          >
          <span
            v-show="errors.has('phone_number')"
            class="error"
          >
            {{ errors.first('phone_number') }}
          </span>
        </div>

        <div class="corporate-checkbox">
          <input
            id="checkout-address-corporate"
            v-model="corporate"
            type="checkbox"
          >
          <label for="checkout-address-corporate">{{ $t('checkout.address.corporate') }}</label>
        </div>

        <div
          v-if="corporate"
          class="corporate-fields"
        >
          <input
            v-model="companyName"
            type="text"
            autocomplete="off"
            :placeholder="$t('checkout.address.companyName')"
            @change="$emit('payment-fields-update', { companyName: companyName })"
          >

          <input
            v-model="purchaseOrderNumber"
            type="text"
            autocomplete="off"
            :placeholder="$t('checkout.address.purchaseOrderNumber')"
            @change="$emit('payment-fields-update', { purchaseOrderNumber: purchaseOrderNumber })"
          >
        </div>

        <div class="required-field">
          <input
            id="checkout_address"
            v-model="address1"
            v-validate="'required'"
            name="address1"
            type="text"
            :placeholder="$t('checkout.address.address1')"
            :class="{ 'required': errors.has('address1') }"
            @change="$emit('payment-fields-update', { address1: address1 })"
          >
          <span
            v-show="errors.has('address1')"
            class="error"
          >
            {{ errors.first('address1') }}
          </span>
        </div>

        <div>
          <input
            v-model="address2"
            type="text"
            :placeholder="$t('checkout.address.address2')"
            @change="$emit('payment-fields-update', { address2: address2 })"
          >
        </div>

        <div class="required-field">
          <input
            id="checkout_city"
            v-model="city"
            v-validate="'required'"
            name="city"
            type="text"
            :placeholder="$t('checkout.address.city')"
            :class="{ 'required': errors.has('city') }"
            @change="$emit('payment-fields-update', { city: city })"
          >
          <span
            v-show="errors.has('city')"
            class="error"
          >
            {{ errors.first('city') }}
          </span>
        </div>

        <div class="required-field">
          <input
            id="checkout_zip_code"
            v-model="zipCode"
            v-validate="'required'"
            name="zip_code"
            type="text"
            :placeholder="$t('checkout.address.zipCode')"
            :class="{ 'required': errors.has('zip_code') }"
            @change="$emit('payment-fields-update', { zipCode: zipCode })"
          >

          <span
            v-show="errors.has('zip_code')"
            class="error"
          >
            {{ errors.first('zip_code') }}
          </span>
        </div>

        <div class="required-field">
          <select
            id="checkout_country"
            v-model="country"
            v-validate="{ required: true, excluded: blockedCountries }"
            name="country"
            :class="{ 'required': errors.has('country') }"
            @change="updateCountryAndState"
          >
            <option
              :value="null"
              disabled
            >
              {{ $t('checkout.address.country') }}
            </option>
            <option value="US">
              {{ countriesList['US'].name }}
            </option>
            <hr>

            <option
              v-for="selectedCountry in countriesList"
              :key="selectedCountry.code"
              :value="selectedCountry.code"
            >
              {{ selectedCountry.translations[currentLocale] || selectedCountry.name }}
            </option>
          </select>
          <span
            v-show="errors.has('country')"
            class="error"
          >
            {{ errors.first('country') }}
          </span>
        </div>

        <div class="required-field">
          <select
            v-if="stateLists.length > 0"
            id="checkout_state"
            v-model="state"
            v-validate="{ is_not: 'missing state value' }"
            name="state"
            :class="{ 'required': errors.has('state') }"
            @change="$emit('payment-fields-update', { state: state })"
          >
            <option
              value="missing state value"
              disabled
            >
              {{ $t('checkout.address.state') }}
            </option>
            <option
              v-for="selectedState in stateLists"
              :key="selectedState.code"
              :value="selectedState.code"
            >
              {{ selectedState.name }}
            </option>
          </select>
          <span
            v-show="errors.has('state')"
            class="error"
          >
            {{ errors.first('state') }}
          </span>
        </div>
      </div>

      <button
        class="continue-button btn btn-primary"
        @click="checkRequiredFields"
      >
        {{ $t('cookie.continue') }}
      </button>
    </div>
  </section>
</template>

<script>
/* global locale */

import CountryData from '../country_data'

export default {

  // eslint-disable-next-line vue/require-prop-types
  props: ['currentsection', 'number', 'hotel'],

  data() {
    return {
      address1: null,
      address2: null,
      city: null,
      companyName: null,
      corporate: false,
      country: null,
      countriesList: [],
      firstName: null,
      lastName: null,
      phoneNumber: null,
      purchaseOrderNumber: null,
      sectionName: 'address',
      showRequired: false,
      stateLists: [],
      state: null,
      zipCode: null
    }
  },
  computed: {
    blockedCountries() {
      return CountryData.blockedCountries().concat(['', null, undefined]).toString()
    },

    currentLocale() {
      return locale.toLowerCase()
    },

    selected() {
      return this.currentsection == this.sectionName
    },

    openClass() {
      return (this.selected) ? 'open' : ''
    }
  },

  created() {
    this.countriesList = CountryData.countryNamesAndCodes()
  },

  methods: {
    checkRequiredFields() {
      this.$validator.validate().then(valid => {
        if (valid) {
          let sectionNext = this.hotel ? 'hotel' : 'review'
          this.updateSection(sectionNext)
        } else {
          // Don't continue with invalid fields
          for (let field of this.$validator.fields) { field.update({ events: 'input|blur' }) }
        }
      })
    },

    updateCountryAndState() {
      CountryData.countryStates(this.country).then(states => {
        this.stateLists = (states == undefined ? [] : states)

        if (this.stateLists.length > 0) {
          this.state = 'missing state value'
        } else {
          this.state = null
        }

        this.$emit('payment-fields-update', { country: this.country, state: this.state })
      })
    },

    updateSection(section) {
      this.$emit('section-update', section)
    }
  }
}
</script>
